import { useState, useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useLocation } from 'react-router-dom';
import { useClientFilter } from '../../context/ClientFilterContext.jsx';
import { useClients } from '../../context/ClientsContext.jsx';
import { ROUTES } from '../../routes.js';

// Derived from routes.js — no manual sync needed.
const CLIENT_SCOPED_PREFIXES = ROUTES
  .filter((r) => r.clientScoped)
  .map((r) => r.path.split('/:')[0]);

function isClientScoped(pathname) {
  return CLIENT_SCOPED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

const ITEM_CLS = [
  'flex items-center justify-between gap-2 px-3 py-1.5 text-[13px] rounded-[5px]',
  'text-n-700 hover:bg-sitruna-yellow/10 hover:text-sitruna-black',
  'cursor-pointer select-none outline-none focus:bg-sitruna-yellow/10',
  'data-[highlighted]:bg-sitruna-yellow/10 data-[highlighted]:text-sitruna-black',
].join(' ');

const CONTENT_CLS = [
  'z-[var(--z-popover)] w-[240px]',
  'bg-white rounded-lg shadow-3 border border-n-100',
  'animate-in fade-in-0 zoom-in-95 duration-100',
].join(' ');

export default function ContextPill() {
  const { pathname } = useLocation();
  const { clients: allClients } = useClients();
  const { clients, clientId, marketplace, marketplaces, setClientId, setMarketplace } =
    useClientFilter();

  const [clientSearch, setClientSearch] = useState('');
  const searchRef = useRef(null);

  const scoped = isClientScoped(pathname);

  const activeClientCount = allClients.filter((c) => c.overall_status !== 'Left').length;
  const selectedClient = clients.find((c) => c.client_id === clientId);

  const filteredClients = clientSearch.trim()
    ? clients.filter((c) =>
        c.brand_name.toLowerCase().includes(clientSearch.toLowerCase())
      )
    : clients;

  function handleClientOpenChange(open) {
    if (!open) setClientSearch('');
    // Focus the search input on open after the DOM settles
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
  }

  if (!scoped) {
    return (
      <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-n-50 border border-n-200 text-[12.5px] font-medium text-n-500 select-none">
        All clients
        <span className="text-n-400">· {activeClientCount}</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* Client picker */}
      <DropdownMenu.Root onOpenChange={handleClientOpenChange}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={[
              'inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12.5px] font-medium',
              'focus:outline-none focus:ring-1 focus:ring-sitruna-yellow transition-colors',
              clientId
                ? 'bg-sitruna-black text-white border border-sitruna-black hover:bg-sitruna-black/90'
                : 'bg-sitruna-yellow text-sitruna-black border border-sitruna-yellow hover:bg-sitruna-yellow/90',
            ].join(' ')}
          >
            <span className="hidden sm:inline max-w-[140px] truncate">
              {selectedClient ? selectedClient.brand_name : 'Select client…'}
            </span>
            <span className="sm:hidden">{selectedClient ? selectedClient.brand_name.split(' ')[0] : '—'}</span>
            <ChevronDown />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content className={CONTENT_CLS} align="start" sideOffset={4}>
            {/* Search input — stopPropagation prevents Radix typeahead capturing keystrokes */}
            <div className="p-2 border-b border-n-100">
              <div className="flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-n-25 border border-n-150 focus-within:border-sitruna-yellow focus-within:ring-1 focus-within:ring-sitruna-yellow">
                <SearchIcon />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search clients…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="flex-1 min-w-0 bg-transparent text-[13px] text-n-700 placeholder:text-n-400 focus:outline-none"
                />
              </div>
            </div>

            {/* Scrollable list */}
            <div className="max-h-[272px] overflow-y-auto p-1">
              {clientId && !clientSearch && (
                <>
                  <DropdownMenu.Item
                    className={ITEM_CLS}
                    onSelect={() => setClientId('')}
                  >
                    <span className="text-n-400 italic">Clear selection</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="my-1 h-px bg-n-100" />
                </>
              )}
              {filteredClients.length === 0 ? (
                <div className="px-3 py-2 text-[13px] text-n-400 italic">No clients match</div>
              ) : (
                filteredClients.map((c) => (
                  <DropdownMenu.Item
                    key={c.client_id}
                    className={ITEM_CLS}
                    onSelect={() => setClientId(c.client_id)}
                  >
                    <span className="truncate">{c.brand_name}</span>
                    {c.client_id === clientId && <CheckIcon />}
                  </DropdownMenu.Item>
                ))
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Marketplace picker — only when a client is selected and has >1 marketplace */}
      {clientId && marketplaces.length > 1 && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-medium bg-white border border-n-200 text-n-600 hover:bg-n-25 hover:border-n-300 focus:outline-none focus:ring-1 focus:ring-sitruna-yellow transition-colors"
            >
              {marketplace || marketplaces[0]}
              <ChevronDown />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={[
                'z-[var(--z-popover)] min-w-[120px]',
                'bg-white rounded-lg shadow-3 border border-n-100 p-1',
                'animate-in fade-in-0 zoom-in-95 duration-100',
              ].join(' ')}
              align="start"
              sideOffset={4}
            >
              {marketplaces.map((m) => (
                <DropdownMenu.Item
                  key={m}
                  className={ITEM_CLS}
                  onSelect={() => setMarketplace(m)}
                >
                  <span>{m}</span>
                  {m === marketplace && <CheckIcon />}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}

      {/* Single marketplace — show as static badge */}
      {clientId && marketplaces.length === 1 && (
        <span className="inline-flex items-center h-7 px-2.5 rounded-full text-[12px] font-medium bg-white border border-n-200 text-n-500 select-none">
          {marketplaces[0]}
        </span>
      )}
    </div>
  );
}
